import { Card, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type SiteRow, type TomorrowEstimateRow } from "../../api.js";
import { addDays, tehranToday } from "../../labels.js";

interface MealRow {
  id: string;
  name: string;
}

export function TomorrowTab() {
  const [targetDate, setTargetDate] = useState(() => addDays(tehranToday(), 1));
  const [kitchenId, setKitchenId] = useState("");
  const [mealId, setMealId] = useState("");
  const [expandedFoodId, setExpandedFoodId] = useState<string | null>(null);

  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });
  const kitchens = (sites.data ?? []).filter((s) => s.kind === "kitchen");

  const meals = useQuery({
    queryKey: ["menu-lists", "meals"],
    queryFn: () => api<MealRow[]>("/menu/lists/meals").catch(() => []),
  });

  const queryParams = new URLSearchParams();
  if (targetDate) queryParams.set("targetDate", targetDate);
  if (kitchenId) queryParams.set("kitchenId", kitchenId);
  if (mealId) queryParams.set("mealId", mealId);

  const tomorrow = useQuery({
    queryKey: ["forecast", "tomorrow", targetDate, kitchenId, mealId],
    queryFn: () => api<TomorrowEstimateRow>(`/forecast/tomorrow?${queryParams.toString()}`),
  });

  const data = tomorrow.data;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Filters */}
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "flex-end" }}>
        <div className="field">
          <label htmlFor="forecast-date">تاریخ هدف (پیش‌بینی فردا)</label>
          <input
            id="forecast-date"
            type="date"
            className="input"
            value={targetDate}
            onChange={(e) => {
              setTargetDate(e.target.value);
            }}
          />
        </div>

        <div className="field">
          <label htmlFor="forecast-kitchen">آشپزخانه</label>
          <select
            id="forecast-kitchen"
            className="input"
            value={kitchenId}
            onChange={(e) => {
              setKitchenId(e.target.value);
            }}
          >
            <option value="">همه آشپزخانه‌ها</option>
            {kitchens.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="forecast-meal">وعده غذایی</label>
          <select
            id="forecast-meal"
            className="input"
            value={mealId}
            onChange={(e) => {
              setMealId(e.target.value);
            }}
          >
            <option value="">پیش‌فرض سیستم</option>
            {(meals.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {tomorrow.isLoading && <p className="ink2">در حال محاسبه و پیش‌بینی...</p>}
      {tomorrow.error && (
        <p className="hint" role="alert" style={{ color: "var(--coral-ink)" }}>
          خطا در دریافت پیش‌بینی: {tomorrow.error.message}
        </p>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "var(--s3)",
            }}
          >
            <Card>
              <div className="stack" style={{ gap: "var(--s1)" }}>
                <span className="hint">کل پرس مورد انتظار</span>
                <span className="t-card num b" style={{ fontSize: "1.5rem" }}>
                  {faDigits(data.totalExpectedPortions)} پرس
                </span>
                <span className="hint ink2">
                  روز هفته: {data.weekdayName} ({data.targetDate})
                </span>
              </div>
            </Card>

            <Card>
              <div className="stack" style={{ gap: "var(--s1)" }}>
                <span className="hint">سفارش ثبت‌شده تاکنون</span>
                <span className="t-card num b" style={{ fontSize: "1.5rem" }}>
                  {faDigits(data.totalOrderedPortions ?? 0)} پرس
                </span>
                <span className="hint ink2">
                  {data.totalOrderedPortions && data.totalExpectedPortions
                    ? `${faDigits(Math.round((data.totalOrderedPortions / data.totalExpectedPortions) * 100))}% سفارش‌ها ثبت شده`
                    : "در انتظار ثبت سفارش واحدها"}
                </span>
              </div>
            </Card>

            <Card>
              <div className="stack" style={{ gap: "var(--s1)" }}>
                <span className="hint">بازه اطمینان پیش‌بینی</span>
                <span className="t-card num b" style={{ fontSize: "1.5rem" }}>
                  {faDigits(data.lowPortions)} تا {faDigits(data.highPortions)}
                </span>
                <span className="hint ink2">دامنه برآورد بر اساس نوسان‌های گذشته</span>
              </div>
            </Card>

            <Card>
              <div className="stack" style={{ gap: "var(--s1)" }}>
                <span className="hint">واحدهای مشارکت‌کننده</span>
                <span className="t-card num b" style={{ fontSize: "1.5rem" }}>
                  {faDigits(data.participatingUnitsCount)} واحد
                </span>
                <span className="hint ink2">واحدهای دارای سابقه در این روز هفته</span>
              </div>
            </Card>
          </div>

          {/* Foods Table */}
          <Card>
            <table className="table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>نام غذا</th>
                  <th>سهم از کل منو</th>
                  <th>پرس مورد انتظار</th>
                  <th>پرس ثبت‌شده تا الان</th>
                  <th>جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {data.foods.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", color: "var(--muted)" }}>
                      هیچ غذایی برای این تاریخ و وعده تعریف نشده است.
                    </td>
                  </tr>
                ) : (
                  data.foods.map((food, idx) => {
                    const isExpanded = expandedFoodId === food.foodId;
                    return (
                      <tr
                        key={food.foodId}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setExpandedFoodId(isExpanded ? null : food.foodId);
                        }}
                      >
                        <td>{faDigits(idx + 1)}</td>
                        <td>
                          <strong>{food.foodName ?? food.foodId}</strong>
                        </td>
                        <td>{faDigits((food.foodSharePerMille / 10).toFixed(1))}%</td>
                        <td className="b num">{faDigits(food.expectedPortions)}</td>
                        <td className="num">{faDigits(food.orderedPortions ?? 0)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedFoodId(isExpanded ? null : food.foodId);
                            }}
                          >
                            {isExpanded ? "بستن توضیحات" : "مشاهده توضیح"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Card>

          {/* Explanation Banner */}
          <Card>
            <div className="stack" style={{ gap: "var(--s2)" }}>
              <h3 className="t-title" style={{ fontSize: "1rem" }}>
                مبنای محاسبه و تاریخچه روزهای استفاده‌شده:
              </h3>
              <p className="ink2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {data.explanation}
              </p>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
